import { motion } from "framer-motion";
import { ShieldAlert, Radar, CarFront, Truck, Anchor, ArrowUpRight } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { EASE, scrollToId } from "./data";

const CARDS = [
  { id: "impound", icon: ShieldAlert, title: "Private Property Impound", text: "Unauthorized vehicles removed from your property and taken to a secured lot — documented, by the book.", span: "lg:col-span-7 lg:row-span-2", img: "/img/impound-lot.jpg" },
  { id: "enforcement", icon: Radar, title: "Parking Enforcement", text: "Regular patrols of fire lanes, reserved and resident-only spaces.", span: "lg:col-span-5" },
  { id: "abandoned", icon: CarFront, title: "Abandoned Vehicles", text: "Dead, expired-tag and long-term abandoned cars cleared out.", span: "lg:col-span-5" },
  { id: "towing", icon: Truck, title: "Towing", text: "Local towing across Charlotte when you need a vehicle moved.", span: "lg:col-span-4" },
  { id: "recovery", icon: Anchor, title: "Recovery", text: "Stuck, ditched or disabled — we winch it out and haul it off.", span: "lg:col-span-4", img: "/img/winch.jpg" },
];

const Card = ({ c, i }) => {
  const Icon = c.icon;
  return (
    <motion.article
      data-testid={`service-card-${c.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, ease: EASE, delay: i * 0.08 }}
      whileHover={{ y: -4 }}
      className={`group relative flex min-h-[260px] flex-col justify-between overflow-hidden border border-[var(--line)] bg-[var(--ink-2)] p-6 transition-[border-color] duration-500 hover:border-[var(--signal)] sm:p-8 ${c.span}`}
    >
      {c.img && (
        <>
          <img src={c.img} alt="" className="absolute inset-0 h-full w-full object-cover opacity-75 transition-[transform,opacity] duration-[1.2s] group-hover:scale-105 group-hover:opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0c] via-[#0b0b0c]/40 to-transparent" />
        </>
      )}
      <div className="relative flex items-start justify-between">
        <span className="font-mono text-xs text-zinc-400">0{i + 1}</span>
        <Icon className="h-7 w-7 text-[var(--signal)]" strokeWidth={1.5} />
      </div>
      <div className="relative">
        <h3 className="font-display text-3xl font-extrabold uppercase leading-none sm:text-4xl">{c.title}</h3>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-zinc-300 sm:text-base">{c.text}</p>
      </div>
    </motion.article>
  );
};

const CtaCard = () => (
  <a href="#quote" onClick={(e) => { e.preventDefault(); scrollToId("quote"); }}
    data-testid="service-card-cta"
    className="hazard group relative flex min-h-[260px] flex-col justify-end p-1 lg:col-span-4">
    <div className="flex h-full flex-col justify-between bg-[var(--signal)] p-6 text-[var(--ink)] sm:p-8">
      <ArrowUpRight className="h-10 w-10 self-end transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1" strokeWidth={2} />
      <p className="font-display text-3xl font-black uppercase leading-[0.95] sm:text-4xl">Put your property on patrol</p>
    </div>
  </a>
);

export const Services = () => (
  <section id="services" data-testid="services-section" className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 lg:py-36">
    <Reveal className="mb-14 grid gap-6 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <SectionLabel index="01">Services</SectionLabel>
        <h2 className="font-display text-5xl font-black uppercase leading-[0.9] sm:text-6xl lg:text-7xl">Built for the<br />lots that matter.</h2>
      </div>
      <p className="self-end text-base leading-relaxed text-zinc-300 lg:col-span-5">
        From one-time removals to ongoing enforcement, Warrior handles the hard part so residents, tenants and customers always find a space.
      </p>
    </Reveal>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:auto-rows-[280px] lg:grid-cols-12">
      {CARDS.map((c, i) => <Card key={c.id} c={c} i={i} />)}
      <CtaCard />
    </div>
  </section>
);
